import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import './style.css';

const RECOMMENDED_QUALITY = 72;
const RECOMMENDED_MAX_WIDTH = 4096;
const OPTIMIZATION_RESTORE_KEY = 'adminToursOptimizationRestore';

function AdminTours() {
    const history = useHistory();
    const [tours, setTours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [publicFolders, setPublicFolders] = useState([]);
    const [baseFolders, setBaseFolders] = useState([]);
    const [imageFilesList, setImageFilesList] = useState([]);
    const [baseAudioFilesList, setBaseAudioFilesList] = useState([]);
    const [sceneImageFilesList, setSceneImageFilesList] = useState([]);
    const [error, setError] = useState(null);

    // Optimization state
    const [optimizationRunning, setOptimizationRunning] = useState(false);
    const [optimizationLog, setOptimizationLog] = useState([]);
    const [optimizationDone, setOptimizationDone] = useState(false);
    const [optimizedCount, setOptimizedCount] = useState(0);
    const logBoxRef = useRef(null);
    const sseRef = useRef(null);
    const restoredOptimizationRef = useRef(false);

    // Tour Form State
    const [tourData, setTourData] = useState({
        id: '',
        name: '',
        baseFolder: 'public/',
        imageFolder: '',
        mainVoFolderMale: '',
        mainVoFolderFemale: '',
        infoVoFolderMale: '',
        infoVoFolderFemale: '',
        thumbnail: '',
        introVideo: '',
        startSceneImage: '',
        backgroundAudioFile: '',
        backgroundAudioVolume: 0.1,
        showTransitionNames: true,
        useOptimizedImages: false,
        optimizationQuality: RECOMMENDED_QUALITY,
        optimizationMaxWidth: RECOMMENDED_MAX_WIDTH,
    });

    useEffect(() => {
        if (sessionStorage.getItem('adminLoggedIn') !== 'true') {
            history.push('/admin/login');
            return;
        }
        fetchTours();
        fetchFolders();
    }, [history]);

    useEffect(() => {
        if (tourData.baseFolder) {
            fetch(`http://localhost:3002/api/image-files?folder=${encodeURIComponent(tourData.baseFolder)}`)
                .then(res => res.json())
                .then(data => { if (Array.isArray(data)) setImageFilesList(data); })
                .catch(err => console.error(err));
        } else {
            setImageFilesList([]);
        }
    }, [tourData.baseFolder]);

    useEffect(() => {
        if (tourData.baseFolder) {
            fetch(`http://localhost:3002/api/audio-files?folder=${encodeURIComponent(tourData.baseFolder)}&includeExtension=true`)
                .then(res => res.json())
                .then(data => { if (Array.isArray(data)) setBaseAudioFilesList(data); })
                .catch(err => console.error(err));
        } else {
            setBaseAudioFilesList([]);
        }
    }, [tourData.baseFolder]);

    useEffect(() => {
        if (tourData.imageFolder) {
            fetch(`http://localhost:3002/api/image-files?folder=${encodeURIComponent(tourData.imageFolder)}`)
                .then(res => res.json())
                .then(data => { if (Array.isArray(data)) setSceneImageFilesList(data); })
                .catch(err => console.error(err));
        } else {
            setSceneImageFilesList([]);
        }
    }, [tourData.imageFolder]);

    // Auto-scroll log box on new lines
    useEffect(() => {
        if (logBoxRef.current) {
            logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
        }
    }, [optimizationLog]);

    useEffect(() => {
        if (loading || showForm || restoredOptimizationRef.current || tours.length === 0) {
            return;
        }

        const pendingRestore = sessionStorage.getItem(OPTIMIZATION_RESTORE_KEY);
        if (!pendingRestore) {
            return;
        }

        try {
            const parsed = JSON.parse(pendingRestore);
            const matchingTour = tours.find((tour) => tour.id === parsed?.tourId);

            if (!matchingTour) {
                sessionStorage.removeItem(OPTIMIZATION_RESTORE_KEY);
                return;
            }

            restoredOptimizationRef.current = true;
            handleEditOpen(matchingTour, parsed?.activeTab || 'optimization');
        } catch (err) {
            console.error('Failed to restore optimization modal state', err);
            sessionStorage.removeItem(OPTIMIZATION_RESTORE_KEY);
        }
    }, [loading, showForm, tours]);

    const fetchTours = async () => {
        try {
            setLoading(true);
            const res = await fetch('http://localhost:3002/api/tours');
            if (res.ok) {
                const data = await res.json();
                setTours(data);
            }
        } catch (err) {
            console.error('Failed to fetch tours', err);
            setError('Could not connect to backend server on port 3002');
        } finally {
            setLoading(false);
        }
    };

    const fetchFolders = async () => {
        try {
            const res = await fetch('http://localhost:3002/api/public-folders');
            if (res.ok) {
                const data = await res.json();
                setPublicFolders(data);
                setBaseFolders(data);
            }
        } catch (err) {
            console.error('Failed to fetch folders', err);
        }
    };

    const fetchOptimizationStatus = useCallback(async (tourId) => {
        try {
            const res = await fetch(`http://localhost:3002/api/optimization-status/${tourId}`);
            if (res.ok) {
                const data = await res.json();
                setOptimizedCount(data.optimizedCount || 0);
                setOptimizationDone(data.hasOptimizedImages);
            }
        } catch (err) {
            console.error('Failed to fetch optimization status', err);
        }
    }, []);

    const resetOptimizationState = () => {
        setOptimizationLog([]);
        setOptimizationRunning(false);
        setOptimizationDone(false);
        setOptimizedCount(0);
        if (sseRef.current) {
            sseRef.current.close();
            sseRef.current = null;
        }
    };

    const handleCreateOpen = () => {
        setError(null);
        setIsEditMode(false);
        setActiveTab('general');
        resetOptimizationState();
        setTourData({
            id: `tour_${Date.now()}`,
            name: '',
            baseFolder: 'public/',
            imageFolder: '',
            mainVoFolderMale: '',
            mainVoFolderFemale: '',
            infoVoFolderMale: '',
            infoVoFolderFemale: '',
            thumbnail: '',
            introVideo: '',
            startSceneImage: '',
            backgroundAudioFile: '',
            backgroundAudioVolume: 0.1,
            showTransitionNames: true,
            useOptimizedImages: false,
            optimizationQuality: RECOMMENDED_QUALITY,
            optimizationMaxWidth: RECOMMENDED_MAX_WIDTH,
        });
        setShowForm(true);
    };

    const handleEditOpen = (tour, startTab = 'general') => {
        setError(null);
        setIsEditMode(true);
        setActiveTab(startTab);
        resetOptimizationState();
        setTourData({
            id: tour.id,
            name: tour.name,
            baseFolder: tour.baseFolder || 'public/',
            imageFolder: tour.imageFolder || '',
            mainVoFolderMale: tour.mainVoFolderMale || tour.mainVoFolder || '',
            mainVoFolderFemale: tour.mainVoFolderFemale || '',
            infoVoFolderMale: tour.infoVoFolderMale || tour.infoVoFolder || '',
            infoVoFolderFemale: tour.infoVoFolderFemale || '',
            thumbnail: tour.thumbnail || '',
            introVideo: tour.introVideo || '',
            startSceneImage: tour.startSceneImage || '',
            backgroundAudioFile: tour.backgroundAudioFile || '',
            backgroundAudioVolume: typeof tour.backgroundAudioVolume === 'number' ? tour.backgroundAudioVolume : 0.1,
            showTransitionNames: tour.showTransitionNames !== false,
            useOptimizedImages: tour.useOptimizedImages || false,
            optimizationQuality: tour.optimizationQuality || RECOMMENDED_QUALITY,
            optimizationMaxWidth: tour.optimizationMaxWidth || RECOMMENDED_MAX_WIDTH,
        });
        // Load existing optimization status
        if (tour.id) fetchOptimizationStatus(tour.id);
        setShowForm(true);
    };

    const persistOptimizationRestoreState = () => {
        sessionStorage.setItem(OPTIMIZATION_RESTORE_KEY, JSON.stringify({
            tourId: tourData.id,
            activeTab: 'optimization',
        }));
    };

    const saveTourSettings = async (tourPayload) => {
        if (!tourPayload.name || !tourPayload.id || !tourPayload.imageFolder || !tourPayload.baseFolder) {
            throw new Error('Please fill all required fields');
        }

        let res;
        if (isEditMode) {
            res = await fetch(`http://localhost:3002/api/tours-meta/${tourPayload.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(tourPayload)
            });
        } else {
            res = await fetch('http://localhost:3002/api/tours', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(tourPayload)
            });
        }

        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.error || 'Failed to save tour');
        }

        return data;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        try {
            setError(null);
            await saveTourSettings(tourData);
            sessionStorage.removeItem(OPTIMIZATION_RESTORE_KEY);
            setShowForm(false);
            fetchTours();
        } catch (err) {
            setError(err.message || 'Error connecting to server');
        }
    };

    const handleDeleteTour = async (tourId, tourName) => {
        if (!window.confirm(`Are you sure you want to delete "${tourName}"? This cannot be undone.`)) return;
        try {
            const res = await fetch(`http://localhost:3002/api/tours/${tourId}`, { method: 'DELETE' });
            if (res.ok) {
                fetchTours();
            } else {
                const data = await res.json();
                setError(data.error || 'Failed to delete tour');
            }
        } catch (err) {
            setError('Error connecting to server');
        }
    };

    const handleLogout = () => {
        sessionStorage.removeItem('adminLoggedIn');
        history.push('/admin/login');
    };

    const handleRunOptimization = async () => {
        if (optimizationRunning) return;
        if (!isEditMode || !tourData.id) {
            setError('Please save the tour once before running optimization.');
            return;
        }

        try {
            setError(null);
            await saveTourSettings(tourData);
            persistOptimizationRestoreState();
            fetchTours();
        } catch (err) {
            setError(err.message || 'Failed to save optimization settings before starting.');
            return;
        }

        setOptimizationLog([]);
        setOptimizationRunning(true);
        setOptimizationDone(false);

        // We use fetch + ReadableStream for SSE (EventSource doesn't support POST body)
        fetch(`http://localhost:3002/api/optimize-images/${tourData.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                quality: tourData.optimizationQuality,
                maxWidth: tourData.optimizationMaxWidth,
            }),
        }).then(async (res) => {
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            const processChunk = (chunk) => {
                buffer += chunk;
                const parts = buffer.split('\n\n');
                buffer = parts.pop(); // keep incomplete last part
                parts.forEach(part => {
                    const line = part.replace(/^data: /, '').trim();
                    if (!line) return;
                    try {
                        const msg = JSON.parse(line);
                        setOptimizationLog(prev => [...prev, msg]);
                        if (msg.type === 'done' || msg.type === 'error') {
                            setOptimizationRunning(false);
                            setOptimizationDone(msg.type === 'done');
                            sessionStorage.removeItem(OPTIMIZATION_RESTORE_KEY);
                            // Refresh count
                            fetchOptimizationStatus(tourData.id);
                        }
                    } catch (_) {}
                });
            };

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    setOptimizationRunning(false);
                    break;
                }
                processChunk(decoder.decode(value, { stream: true }));
            }
        }).catch(err => {
            setOptimizationLog(prev => [...prev, { type: 'error', message: err.message }]);
            setOptimizationRunning(false);
            sessionStorage.removeItem(OPTIMIZATION_RESTORE_KEY);
        });
    };

    const getSubFolders = () => {
        if (!tourData.baseFolder) return publicFolders;
        return publicFolders.filter(f => f.startsWith(tourData.baseFolder) && f !== tourData.baseFolder);
    };

    const logLineClass = (type) => {
        if (type === 'error') return 'opt-log-error';
        if (type === 'warning') return 'opt-log-warning';
        if (type === 'done') return 'opt-log-done';
        if (type === 'start' || type === 'info') return 'opt-log-info';
        return 'opt-log-progress';
    };

    if (loading) {
        return <div className="admin-tours-container"><div className="admin-tours-loader">Loading tours...</div></div>;
    }

    return (
        <div className="admin-tours-container">
            <div className="admin-topbar">
                <div className="admin-topbar-left">
                    <span className="admin-topbar-title">🌍 Tour Manager</span>
                </div>
                <div className="admin-topbar-right">
                    <button className="admin-topbar-btn" onClick={handleLogout}>Logout</button>
                </div>
            </div>

            <div className="admin-tours-content">
                <div className="admin-tours-header">
                    <h2>Virtual Tours</h2>
                    <button className="admin-btn-primary" onClick={handleCreateOpen}>
                        + Create New Tour
                    </button>
                </div>

                {error && !showForm && <div className="admin-error-banner">{error}</div>}

                <div className="admin-tours-grid">
                    {tours.length === 0 ? (
                        <div className="admin-tours-empty">
                            <p>No tours found. Create one to get started!</p>
                        </div>
                    ) : (
                        tours.map(tour => (
                            <div key={tour.id} className="admin-tour-card">
                                <h3>{tour.name}</h3>
                                {tour.thumbnail && (
                                    <div className="admin-tour-thumbnail" style={{
                                        height: '100px',
                                        backgroundImage: `url("${window.location.origin}/${((tour.baseFolder || '').replace(/^public\/?/, '') + '/' + tour.thumbnail).replace(/^\//, '')}")`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        marginBottom: '10px',
                                        borderRadius: '4px'
                                    }}></div>
                                )}
                                <div className="admin-tour-meta">
                                    <p><strong>Scenes:</strong> {tour.sceneCount}</p>
                                    <p><strong>Images:</strong> {tour.imageFolder}</p>
                                    <p><strong>Created:</strong> {new Date(tour.createdAt).toLocaleDateString()}</p>
                                    {tour.useOptimizedImages && (
                                        <p className="admin-opt-badge">⚡ Optimized Images Active</p>
                                    )}
                                </div>
                                <div className="admin-tour-actions">
                                    <button
                                        className="admin-btn-secondary"
                                        onClick={() => window.open(`${window.location.origin}/?tour=${tour.id}`, '_blank')}
                                    >
                                        View Public
                                    </button>
                                    <button
                                        className="admin-btn-primary"
                                        onClick={() => handleEditOpen(tour)}
                                    >
                                        Edit Settings ⚙️
                                    </button>
                                    <button
                                        className="admin-btn-primary"
                                        onClick={() => history.push(`/admin/editor/${tour.id}`)}
                                    >
                                        Edit Hotspots ✏️
                                    </button>
                                    <button
                                        className="admin-btn-danger"
                                        onClick={() => handleDeleteTour(tour.id, tour.name)}
                                    >
                                        🗑️ Delete
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {showForm && (
                <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
                    <div className="admin-modal">
                        {/* Sticky Header */}
                        <div className="admin-modal-header">
                            <div>
                                <h3>{isEditMode ? 'Edit Tour Settings' : 'Create New Tour'}</h3>
                                {isEditMode && <span className="admin-modal-subtitle">{tourData.name}</span>}
                            </div>
                            <button type="button" className="admin-modal-close" onClick={() => setShowForm(false)}>×</button>
                        </div>

                        {/* Tab Bar */}
                        <div className="admin-tab-bar">
                            <button
                                type="button"
                                className={`admin-tab-btn ${activeTab === 'general' ? 'active' : ''}`}
                                onClick={() => setActiveTab('general')}
                            >
                                ⚙️ General
                            </button>
                            <button
                                type="button"
                                className={`admin-tab-btn ${activeTab === 'optimization' ? 'active' : ''}`}
                                onClick={() => setActiveTab('optimization')}
                            >
                                🖼 Images & Optimization
                                {tourData.useOptimizedImages && <span className="admin-tab-badge">ON</span>}
                            </button>
                        </div>

                        {/* Scrollable Body */}
                        <div className="admin-modal-body">
                            <form onSubmit={handleFormSubmit} id="tour-settings-form">
                                {error && <div className="admin-error-banner">{error}</div>}

                                {/* ======= GENERAL TAB ======= */}
                                {activeTab === 'general' && (
                                    <>
                                        <div className="admin-form-section-title">Basic Info</div>

                                        <div className="admin-form-group">
                                            <label>Tour Name *</label>
                                            <input
                                                type="text"
                                                value={tourData.name}
                                                onChange={e => setTourData({ ...tourData, name: e.target.value })}
                                                placeholder="e.g. Imam Hussain Shrine 2026"
                                                required
                                            />
                                        </div>

                                        <div className="admin-form-group">
                                            <label>Tour ID (URL Slug) *</label>
                                            <input
                                                type="text"
                                                value={tourData.id}
                                                onChange={e => setTourData({ ...tourData, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                                                placeholder="e.g. shrine-2026"
                                                disabled={isEditMode}
                                                required
                                            />
                                            {!isEditMode && <small>Used in URLs, max 30 chars, no spaces.</small>}
                                        </div>

                                        <div className="admin-form-group">
                                            <label>Base Folder *</label>
                                            <input
                                                list="base-folders"
                                                value={tourData.baseFolder}
                                                onChange={e => setTourData({ ...tourData, baseFolder: e.target.value })}
                                                placeholder="e.g. public/tours/my-tour"
                                                required
                                            />
                                            <datalist id="base-folders">
                                                {baseFolders.map(folder => <option key={folder} value={folder} />)}
                                            </datalist>
                                            <small>The root folder for this tour.</small>
                                        </div>

                                        <div className="admin-form-group">
                                            <label>Thumbnail Image</label>
                                            <input
                                                list="thumbnail-files"
                                                value={tourData.thumbnail}
                                                onChange={e => setTourData({ ...tourData, thumbnail: e.target.value })}
                                                placeholder="e.g. thumbnail.jpg"
                                            />
                                            <datalist id="thumbnail-files">
                                                {imageFilesList.map(file => <option key={file} value={file} />)}
                                            </datalist>
                                            <small>Shown on the public tour selection screen.</small>
                                        </div>

                                        <div className="admin-form-group">
                                            <label>Introductory Video URL (Optional)</label>
                                            <input
                                                type="text"
                                                value={tourData.introVideo}
                                                onChange={e => setTourData({ ...tourData, introVideo: e.target.value })}
                                                placeholder="e.g. Google Drive or YouTube link"
                                            />
                                            <small>Video played before entering the tour.</small>
                                        </div>

                                        <div className="admin-form-divider" />
                                        <div className="admin-form-section-title">Scenes</div>

                                        <div className="admin-form-group">
                                            <label>360° Images Folder *</label>
                                            <input
                                                list="image-folders"
                                                value={tourData.imageFolder}
                                                onChange={e => setTourData({ ...tourData, imageFolder: e.target.value })}
                                                placeholder="e.g. public/tours/my-tour/images"
                                                required
                                            />
                                            <datalist id="image-folders">
                                                {getSubFolders().map(folder => <option key={folder} value={folder} />)}
                                            </datalist>
                                            {isEditMode
                                                ? <small>Changing this will not automatically re-scan scenes. Edit hotspots directly.</small>
                                                : <small>Backend will auto-detect all images in this folder to build the initial scene list.</small>
                                            }
                                        </div>

                                        <div className="admin-form-group">
                                            <label>Starting Scene Image</label>
                                            <input
                                                list="start-scene-images"
                                                value={tourData.startSceneImage}
                                                onChange={e => setTourData({ ...tourData, startSceneImage: e.target.value })}
                                                placeholder="e.g. 01 Entrance.webp"
                                            />
                                            <datalist id="start-scene-images">
                                                {sceneImageFilesList.map(file => <option key={file} value={file} />)}
                                            </datalist>
                                            <small>Leave empty to start from the first scene.</small>
                                        </div>

                                        <div className="admin-form-divider" />
                                        <div className="admin-form-section-title">Audio</div>

                                        <div className="admin-form-group">
                                            <label>Background Music / Ambience</label>
                                            <input
                                                list="base-audio-files"
                                                value={tourData.backgroundAudioFile}
                                                onChange={e => setTourData({ ...tourData, backgroundAudioFile: e.target.value })}
                                                placeholder="e.g. ambience.mp3"
                                            />
                                            <datalist id="base-audio-files">
                                                {baseAudioFilesList.map(file => <option key={file} value={file} />)}
                                            </datalist>
                                            <small>Leave empty to disable background music.</small>
                                        </div>

                                        <div className="admin-form-group">
                                            <label>Background Music Volume: {Math.round((tourData.backgroundAudioVolume || 0) * 100)}%</label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                step="1"
                                                value={Math.round((tourData.backgroundAudioVolume || 0) * 100)}
                                                onChange={e => setTourData({ ...tourData, backgroundAudioVolume: Number(e.target.value) / 100 })}
                                            />
                                        </div>

                                        <div className="admin-form-divider" />
                                        <div className="admin-form-section-title">Display</div>

                                        <div className="admin-form-group">
                                            <label className="admin-toggle-label">
                                                <div className={`admin-toggle-pill ${tourData.showTransitionNames ? 'on' : ''}`}
                                                    onClick={() => setTourData({ ...tourData, showTransitionNames: !tourData.showTransitionNames })}>
                                                    <div className="admin-toggle-knob" />
                                                </div>
                                                Show transition hotspot names
                                            </label>
                                            <small>When enabled, scene transition nodes show names on hover.</small>
                                        </div>

                                        <div className="admin-form-divider" />
                                        <div className="admin-form-section-title">Voice Overs</div>

                                        <div className="admin-form-group">
                                            <label>Main Voiceover Folder 👨 (Male)</label>
                                            <input
                                                list="vo-folders"
                                                value={tourData.mainVoFolderMale}
                                                onChange={e => setTourData({ ...tourData, mainVoFolderMale: e.target.value })}
                                                placeholder="e.g. public/tours/my-tour/audio_male"
                                            />
                                            <datalist id="vo-folders">
                                                {getSubFolders().map(folder => <option key={folder} value={folder} />)}
                                            </datalist>
                                        </div>

                                        <div className="admin-form-group">
                                            <label>Main Voiceover Folder 👩 (Female)</label>
                                            <input
                                                list="vo-folders"
                                                value={tourData.mainVoFolderFemale}
                                                onChange={e => setTourData({ ...tourData, mainVoFolderFemale: e.target.value })}
                                                placeholder="e.g. public/tours/my-tour/audio_female"
                                            />
                                        </div>

                                        <div className="admin-form-group">
                                            <label>Info Spot Audio Folder 👨 (Male)</label>
                                            <input
                                                list="infovo-folders"
                                                value={tourData.infoVoFolderMale}
                                                onChange={e => setTourData({ ...tourData, infoVoFolderMale: e.target.value })}
                                                placeholder="e.g. public/tours/my-tour/hotspots_male"
                                            />
                                            <datalist id="infovo-folders">
                                                {getSubFolders().map(folder => <option key={folder} value={folder} />)}
                                            </datalist>
                                        </div>

                                        <div className="admin-form-group">
                                            <label>Info Spot Audio Folder 👩 (Female)</label>
                                            <input
                                                list="infovo-folders"
                                                value={tourData.infoVoFolderFemale}
                                                onChange={e => setTourData({ ...tourData, infoVoFolderFemale: e.target.value })}
                                                placeholder="e.g. public/tours/my-tour/hotspots_female"
                                            />
                                        </div>
                                    </>
                                )}

                                {/* ======= OPTIMIZATION TAB ======= */}
                                {activeTab === 'optimization' && (
                                    <>
                                        <div className="admin-form-section-title">Image Source</div>

                                        {/* Master toggle */}
                                        <div className="admin-opt-toggle-card">
                                            <div className="admin-opt-toggle-info">
                                                <span className="admin-opt-toggle-title">Use Optimized Images</span>
                                                <span className="admin-opt-toggle-desc">
                                                    {tourData.useOptimizedImages
                                                        ? 'Tour is currently serving WebP-optimized images.'
                                                        : 'Tour is currently serving original images.'}
                                                </span>
                                            </div>
                                            <div
                                                className={`admin-toggle-pill large ${tourData.useOptimizedImages ? 'on' : ''}`}
                                                onClick={() => setTourData({ ...tourData, useOptimizedImages: !tourData.useOptimizedImages })}
                                            >
                                                <div className="admin-toggle-knob" />
                                            </div>
                                        </div>

                                        {optimizedCount > 0 && (
                                            <div className="admin-opt-status-bar">
                                                <span>✅ {optimizedCount} optimized image{optimizedCount !== 1 ? 's' : ''} exist in output folder.</span>
                                                {tourData.useOptimizedImages
                                                    ? <span className="admin-opt-active-tag">ACTIVE</span>
                                                    : <span className="admin-opt-inactive-tag">INACTIVE</span>
                                                }
                                            </div>
                                        )}

                                        <div className="admin-form-divider" />
                                        <div className="admin-form-section-title">Optimization Settings</div>

                                        {/* Quality Slider */}
                                        <div className="admin-form-group">
                                            <label className="admin-opt-label-row">
                                                <span>WebP Quality: <strong>{tourData.optimizationQuality}</strong></span>
                                                {tourData.optimizationQuality === RECOMMENDED_QUALITY && (
                                                    <span className="admin-recommended-badge">✓ Recommended</span>
                                                )}
                                            </label>
                                            <input
                                                type="range"
                                                min="30"
                                                max="95"
                                                step="1"
                                                value={tourData.optimizationQuality}
                                                onChange={e => setTourData({ ...tourData, optimizationQuality: Number(e.target.value) })}
                                                className="admin-range-track"
                                            />
                                            <div className="admin-range-labels">
                                                <span>30 (Smallest)</span>
                                                <span>95 (Largest)</span>
                                            </div>
                                            <small>Higher quality = larger file size. 72 is the sweet spot for 360° panoramas.</small>
                                        </div>

                                        {/* Max Width */}
                                        <div className="admin-form-group">
                                            <label className="admin-opt-label-row">
                                                <span>Max Width (px): <strong>{tourData.optimizationMaxWidth}</strong></span>
                                                {tourData.optimizationMaxWidth === RECOMMENDED_MAX_WIDTH && (
                                                    <span className="admin-recommended-badge">✓ Recommended</span>
                                                )}
                                            </label>
                                            <div className="admin-width-presets">
                                                {[2048, 4096, 8192].map(w => (
                                                    <button
                                                        key={w}
                                                        type="button"
                                                        className={`admin-preset-btn ${tourData.optimizationMaxWidth === w ? 'active' : ''}`}
                                                        onClick={() => setTourData({ ...tourData, optimizationMaxWidth: w })}
                                                    >
                                                        {w}px
                                                    </button>
                                                ))}
                                            </div>
                                            <input
                                                type="number"
                                                min="512"
                                                max="16384"
                                                step="256"
                                                value={tourData.optimizationMaxWidth}
                                                onChange={e => setTourData({ ...tourData, optimizationMaxWidth: Number(e.target.value) })}
                                            />
                                            <small>Images wider than this will be resized. Images at or below this width keep their original resolution.</small>
                                        </div>

                                        <div className="admin-form-divider" />
                                        <div className="admin-form-section-title">Run Optimization</div>

                                        {optimizedCount > 0 && (
                                            <div className="admin-opt-overwrite-notice">
                                                ♻️ Re-running will <strong>overwrite</strong> the {optimizedCount} existing optimized image{optimizedCount !== 1 ? 's' : ''}.
                                            </div>
                                        )}

                                        {!tourData.imageFolder && (
                                            <div className="admin-opt-overwrite-notice">
                                                ⚠️ Please set the 360° Images Folder in the General tab first.
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            className={`admin-btn-optimize ${optimizationRunning ? 'running' : ''}`}
                                            onClick={handleRunOptimization}
                                            disabled={optimizationRunning || !tourData.imageFolder}
                                        >
                                            {optimizationRunning
                                                ? <><span className="admin-spinner" />Optimizing…</>
                                                : optimizationDone
                                                    ? '✅ Re-optimize Images'
                                                    : '⚡ Optimize Images Now'
                                            }
                                        </button>

                                        {/* Log Box */}
                                        {optimizationLog.length > 0 && (
                                            <div className="admin-opt-log-box" ref={logBoxRef}>
                                                {optimizationLog.map((entry, i) => (
                                                    <div key={i} className={`admin-opt-log-line ${logLineClass(entry.type)}`}>
                                                        {entry.type === 'progress' ? '  ' : entry.type === 'done' ? '✅ ' : entry.type === 'error' ? '❌ ' : entry.type === 'warning' ? '⚠️ ' : 'ℹ️ '}
                                                        {entry.message}
                                                    </div>
                                                ))}
                                                {optimizationDone && (
                                                    <div className="admin-opt-log-line opt-log-done">
                                                        — Optimization finished. {optimizedCount} images ready in output folder.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </form>
                        </div>

                        {/* Sticky Footer */}
                        <div className="admin-modal-footer">
                            <button type="button" className="admin-btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                            <button type="submit" form="tour-settings-form" className="admin-btn-primary">
                                {isEditMode ? 'Save Settings' : 'Create Tour'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminTours;
